/**
 * Feature Flag Store Module
 * Provides MobX-based feature flag management
 *
 * @module FeatureFlagStore
 */

import { makeAutoObservable, runInAction } from 'mobx';

/**
 * Feature Flag Enumeration
 * Defines available feature flags in the application
 *
 * @enum {string}
 * @property {string} AUTHORING_SMART_EDITOR - Controls the availability of authoring smart editor features
 */
export enum FeatureFlag {
  AUTHORING_SMART_EDITOR = 'AUTHORING_SMART_EDITOR',
  // Other feature flags goes here
}

/**
 * Feature flags type definition
 */
type FeatureFlags = {
  [key in FeatureFlag]: boolean;
};

/**
 * Feature Flag Store Interface
 * Defines the public API for feature flag management
 *
 * @interface IFeatureFlagStore
 */
export interface IFeatureFlagStore {
  isLoading: boolean;
  error: Error | null;
  isEnabled: (flag: FeatureFlag) => boolean;
  enable: (flag: FeatureFlag) => void;
  disable: (flag: FeatureFlag) => void;
}

/**
 * Global store for feature flag management
 * Uses MobX for reactive state management
 *
 * @class FeatureFlagStore
 * @implements {IFeatureFlagStore}
 *
 * @example
 * // Basic usage
 * const store = new FeatureFlagStore();
 * store.initialize();
 * store.isEnabled(FeatureFlag.SMART_EDITOR);
 */
class FeatureFlagStore implements IFeatureFlagStore {
  /** Internal storage for feature flag states */
  private flags: FeatureFlags = {
    [FeatureFlag.AUTHORING_SMART_EDITOR]: false,
  };

  /** Loading state indicator */
  isLoading = true;

  /** Error state */
  error: Error | null = null;

  constructor() {
    makeAutoObservable(this);
    this.initialize();
  }

  /**
   * Check if a feature flag is enabled
   *
   * @param {FeatureFlag} flag - The feature flag to check
   * @returns {boolean} Current state of the feature flag
   */
  isEnabled(flag: FeatureFlag): boolean {
    const stored = localStorage.getItem('featureFlags');
    if (stored) {
      const flags = JSON.parse(stored);
      return flags[flag];
    }
    return this.flags[flag];
  }

  /**
   * Enable a feature flag
   *
   * @param {FeatureFlag} flag - The feature flag to enable
   */
  enable(flag: FeatureFlag): void {
    runInAction(() => {
      this.flags[flag] = true;
      localStorage.setItem('featureFlags', JSON.stringify(this.flags));
    });
  }

  /**
   * Disable a feature flag
   *
   * @param {FeatureFlag} flag - The feature flag to disable
   */
  disable(flag: FeatureFlag): void {
    runInAction(() => {
      this.flags[flag] = false;
      localStorage.setItem('featureFlags', JSON.stringify(this.flags));
    });
  }

  /**
   * Initialize feature flags from storage
   *
   * @returns {Promise<void>} Promise that resolves when flags are loaded
   */
  async initialize(): Promise<void> {
    try {
      const stored = localStorage.getItem('featureFlags');

      runInAction(() => {
        if (stored) {
          this.flags = JSON.parse(stored);
        } else {
          // Store initial flags if nothing in localStorage
          localStorage.setItem('featureFlags', JSON.stringify(this.flags));
        }
        this.isLoading = false;
        this.error = null;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error as Error;
        this.isLoading = false;
      });
      console.error('Failed to initialize feature flags:', error);
    }
  }
}

export const featureFlagStore = new FeatureFlagStore();
